/**
 * Owner: Masa Bayu
 * File: ReaderProfileSettings.tsx
 * Date:Time: 2026-10-22 10:22
 * Description: §393 — seksyen Tetapan Profil Reader: bahasa/mata wang nyata
 *   (resolver §381), pilihan bacaan wired ke store Natural Reader, pembayaran
 *   (label DEMO jujur), peranti/penyegerakan, mod penulis + log keluar.
 *   §418 — pemilih bahasa: dropdown ISO 639-1 (~180 bahasa) dengan gerbang
 *   jujur — bahasa tersijil punya label UI, selain itu fallback + nota.
 *   §457 F4 — log keluar membuang sesi hub (`hub-session`), bukan `/backend`.
 *   §457 F6-C — kad Akaun membawa zon "Batalkan akaun" (§453, re-auth kata
 *   laluan).
 *   §513 — daftar/mode penerbit dibuang dari sini: ia kini seksyen Publishing
 *   pada halaman yang sama, jadi Tetapan hanya urus akaun + log keluar.
 */

import { useState, type ReactElement } from 'react';
import { logoutSession } from '../auth/hub-session';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import { CancelAccountPanel } from './CancelAccountPanel';
import {
  isUiCatalogCertified,
  type MarketplaceLocaleProfile,
} from '../home/marketplace/marketplace-locale';
import {
  WORLD_LANGUAGES,
  findWorldLanguage,
  worldLanguageLabel,
} from '../home/marketplace/world-languages';
import {
  setNaturalReaderPrefs,
  NATURAL_FONT_SCALE_MIN,
  NATURAL_FONT_SCALE_MAX,
  NATURAL_LINE_SPACING_MIN,
  NATURAL_LINE_SPACING_MAX,
  type NaturalReaderPrefs,
  type NaturalTheme,
} from '../reader/natural/natural-reader-prefs';
import type { ReaderProfileData } from './reader-profile-data';
import type { ReaderProfileLabels } from './reader-profile-labels';

/** §418 — senarai dropdown: katalog tersijil dahulu, kemudian bahasa lain (A–Z). */
const CERTIFIED_LANGUAGES = Object.freeze(
  WORLD_LANGUAGES.filter((entry) => isUiCatalogCertified(entry.tag)),
);
const PENDING_LANGUAGES = Object.freeze(
  WORLD_LANGUAGES.filter((entry) => !isUiCatalogCertified(entry.tag)).sort((a, b) =>
    a.englishName.localeCompare(b.englishName),
  ),
);

function Row({
  label,
  value,
  children,
}: {
  readonly label: string;
  readonly value: string;
  readonly children?: ReactElement | readonly ReactElement[];
}): ReactElement {
  return (
    <li className="qxr__setrow">
      <span className="qxr__setlabel">{label}</span>
      <span className="qxr__setvalue">{value}</span>
      {children != null ? <span className="qxr__setctrl">{children}</span> : null}
    </li>
  );
}

function Stepper({
  value,
  format,
  onStep,
  ariaLabel,
}: {
  readonly value: number;
  readonly format: (v: number) => string;
  readonly onStep: (delta: number) => void;
  readonly ariaLabel: string;
}): ReactElement {
  return (
    <span className="qxr__stepper" role="group" aria-label={ariaLabel}>
      <button type="button" aria-label={`${ariaLabel} −`} onClick={() => onStep(-1)}>
        −
      </button>
      <output>{format(value)}</output>
      <button type="button" aria-label={`${ariaLabel} +`} onClick={() => onStep(1)}>
        +
      </button>
    </span>
  );
}

export function ReaderProfileSettings({
  data,
  labels,
  locale,
  onLanguageChange,
  onNavigate,
  onSignOut,
}: {
  readonly data: ReaderProfileData;
  readonly labels: ReaderProfileLabels;
  readonly locale: MarketplaceLocaleProfile;
  readonly onLanguageChange: (tagOrAuto: string) => void;
  readonly onNavigate: (path: string) => void;
  readonly onSignOut: () => void;
}): ReactElement {
  const [prefs, setPrefs] = useState<NaturalReaderPrefs>(data.prefs);
  const lang = locale.language;
  const patch = (p: Partial<NaturalReaderPrefs>): void => {
    setPrefs(setNaturalReaderPrefs(p));
  };
  const stepScale = (delta: number): void => {
    const next = Math.min(
      NATURAL_FONT_SCALE_MAX,
      Math.max(NATURAL_FONT_SCALE_MIN, Math.round((prefs.fontScale + delta * 0.1) * 10) / 10),
    );
    patch({ fontScale: next });
  };
  const stepSpacing = (delta: number): void => {
    const next = Math.min(
      NATURAL_LINE_SPACING_MAX,
      Math.max(NATURAL_LINE_SPACING_MIN, Math.round((prefs.lineSpacing + delta * 0.1) * 10) / 10),
    );
    patch({ lineSpacing: next });
  };
  const themes: readonly { id: NaturalTheme; label: string }[] = [
    { id: 'light', label: labels.themeLight },
    { id: 'sepia', label: labels.themeSepia },
    { id: 'dark', label: labels.themeDark },
  ];
  return (
    <div className="qxr__stack">
      <section className="qxr__card" aria-labelledby="qxr-locale">
        <h2 id="qxr-locale" className="qxr__section-title">
          <LandingIcon name="globe" size={18} />{' '}
          {lang === 'ms' ? 'Bahasa & Mata Wang' : 'Language & Currency'}
        </h2>
        <ul className="qxr__settings">
          <li className="qxr__setrow">
            <span className="qxr__setlabel" id="qxr-lang-label">
              {labels.settingsLanguage}
            </span>
            <select
              className="qxr__select"
              aria-labelledby="qxr-lang-label"
              value={locale.interfaceTag}
              onChange={(event) => {
                onLanguageChange(event.target.value);
              }}
            >
              <option value="auto">{labels.languageAuto}</option>
              <optgroup
                label={lang === 'ms' ? 'Terjemahan UI tersedia' : 'Interface text available'}
              >
                {CERTIFIED_LANGUAGES.map((entry) => (
                  <option key={entry.tag} value={entry.tag}>
                    {worldLanguageLabel(entry)}
                  </option>
                ))}
              </optgroup>
              <optgroup
                label={
                  lang === 'ms'
                    ? 'Bahasa dunia (terjemahan UI belum selesai)'
                    : 'World languages (interface text pending)'
                }
              >
                {PENDING_LANGUAGES.map((entry) => (
                  <option key={entry.tag} value={entry.tag}>
                    {worldLanguageLabel(entry)}
                  </option>
                ))}
              </optgroup>
            </select>
          </li>
          <Row label={labels.settingsCurrency} value={locale.currency} />
        </ul>
        {locale.uiTranslation === 'fallback' ? (
          <p className="qxr__note">
            <LandingIcon name="clock" size={14} />{' '}
            {labels.languagePending.replace(
              '{name}',
              findWorldLanguage(locale.interfaceTag)?.englishName ?? locale.interfaceTag,
            )}
          </p>
        ) : null}
        <p className="qxr__muted">{labels.localeAuto}</p>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-reader">
        <h2 id="qxr-reader" className="qxr__section-title">
          <LandingIcon name="bookOpen" size={18} /> {labels.settingsReader}
        </h2>
        <ul className="qxr__settings">
          <li className="qxr__setrow">
            <span className="qxr__setlabel">{labels.readerTheme}</span>
            <span className="qxr__seg" role="group" aria-label={labels.readerTheme}>
              {themes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-active={prefs.theme === t.id ? 'true' : 'false'}
                  onClick={() => {
                    patch({ theme: t.id });
                  }}
                >
                  {t.label}
                </button>
              ))}
            </span>
          </li>
          <li className="qxr__setrow">
            <span className="qxr__setlabel">{labels.readerFont}</span>
            <span className="qxr__seg" role="group" aria-label={labels.readerFont}>
              <button
                type="button"
                data-active={prefs.fontFamily === 'serif' ? 'true' : 'false'}
                onClick={() => {
                  patch({ fontFamily: 'serif' });
                }}
              >
                {labels.fontSerif}
              </button>
              <button
                type="button"
                data-active={prefs.fontFamily === 'sans' ? 'true' : 'false'}
                onClick={() => {
                  patch({ fontFamily: 'sans' });
                }}
              >
                {labels.fontSans}
              </button>
            </span>
          </li>
          <Row label={labels.readerScale} value="">
            <Stepper
              value={prefs.fontScale}
              format={(v) => `${String(Math.round(v * 100))}%`}
              onStep={stepScale}
              ariaLabel={labels.readerScale}
            />
          </Row>
          <Row label={labels.readerSpacing} value="">
            <Stepper
              value={prefs.lineSpacing}
              format={(v) => `${String(v.toFixed(1))}×`}
              onStep={stepSpacing}
              ariaLabel={labels.readerSpacing}
            />
          </Row>
        </ul>
        <button
          type="button"
          className="qxr__cta qxr__cta--ghost"
          onClick={() => {
            const first = data.continueReading[0] ?? data.shelf[0];
            onNavigate(
              first != null
                ? `/read/${encodeURIComponent(first.item.publicationId)}`
                : '/all-books',
            );
          }}
        >
          {labels.openBookEnvironment} <LandingIcon name="chevronRight" size={12} />
        </button>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-pay">
        <h2 id="qxr-pay" className="qxr__section-title">
          <LandingIcon name="card" size={18} /> {labels.settingsPayment}
        </h2>
        <p className="qxr__empty">{labels.paymentDemo}</p>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-dev">
        <h2 id="qxr-dev" className="qxr__section-title">
          <LandingIcon name="devices" size={18} /> {labels.settingsDevices}
        </h2>
        <ul className="qxr__settings">
          <Row label={labels.thisDevice} value={lang === 'ms' ? 'Aktif' : 'Active'} />
        </ul>
        <p className="qxr__muted">{labels.syncNote}</p>
      </section>

      <section className="qxr__card" aria-labelledby="qxr-acc">
        <h2 id="qxr-acc" className="qxr__section-title">
          <LandingIcon name="lock" size={18} /> {lang === 'ms' ? 'Akaun' : 'Account'}
        </h2>
        <div className="qxr__actions">
          <button
            type="button"
            className="qxr__cta qxr__cta--ghost"
            onClick={() => {
              // §415 — logout mesti batal sesi + padam token disimpan,
              // bukan hanya state React (header home fetch semula daripada token).
              void logoutSession().finally(() => {
                onSignOut();
                onNavigate('/');
              });
            }}
          >
            {labels.signOut}
          </button>
        </div>
        <CancelAccountPanel
          labels={labels}
          hasPassword={data.identity.hasPassword}
          onCancelled={() => {
            // §453 200 — hub sudah padam akaun + prune sesi; clearHubAuth()
            // dipanggil dalam klien, jadi tinggal buang keadaan React + pulang.
            onSignOut();
            onNavigate('/');
          }}
        />
      </section>
    </div>
  );
}
